import React, { useEffect, useMemo, useState } from "react";
import Card from "components/card";

function initials(name = "") {
  return name
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

const StudentList = ({ teamData = [], poaps = [], loading = false }) => {
  const [teams, setTeams] = useState([]);
  const [sortOrder, setSortOrder] = useState("desc"); // asc | desc (by progress)
  const [query, setQuery] = useState("");
  const [poapFilter, setPoapFilter] = useState("All");

  // Responsive pagination
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const PAGE_SIZE = isMobile ? 10 : 15; // Smaller page size for mobile
  const [page, setPage] = useState(0);

  // Handle screen resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (Array.isArray(teamData)) setTeams(teamData);
  }, [teamData]);

  // ---------- Helpers (POINTS-BASED) ----------
  const flattenSteps = (categories = []) =>
    (categories || []).flatMap((c) => c.steps || []);

  // Use totalPoints if numeric; otherwise derive from step points
  const sumPoapTotalPoints = (poap) => {
    if (!poap) return 0;
    const numericTotal = Number(poap.totalPoints);
    if (!Number.isNaN(numericTotal) && numericTotal > 0) return numericTotal;
    const steps = flattenSteps(poap.categories || []);
    return steps.reduce((s, st) => s + (Number(st.points) || 0), 0);
  };

  // Total points for "All" scope
  const allPoapsTotalPoints = useMemo(
    () => (poaps || []).reduce((sum, p) => sum + sumPoapTotalPoints(p), 0),
    [poaps]
  );

  // Build a step->points map for the chosen scope
  const buildStepPointsMap = (scopePoaps) => {
    const steps = (scopePoaps || []).flatMap((p) => flattenSteps(p.categories || []));
    const map = new Map();
    for (const s of steps) {
      const id = String(s.id);
      const pts = Number(s.points) || 0;
      // if a step id appears multiple times across POAPs, accumulate
      map.set(id, (map.get(id) || 0) + pts);
    }
    return map;
  };

  // ---------- Compute progress per team (achieved points / total points) ----------
  const withProgress = useMemo(() => {
    const selectedPoap =
      poapFilter === "All" ? null : poaps.find((p) => p.id === parseInt(poapFilter, 10));

    // Determine denominator (total points in scope) and step->points map for numerator
    let totalPointsInScope = 0;
    let stepPointsMap;

    if (poapFilter === "All") {
      totalPointsInScope = allPoapsTotalPoints;
      stepPointsMap = buildStepPointsMap(poaps);
    } else {
      totalPointsInScope = sumPoapTotalPoints(selectedPoap);
      stepPointsMap = buildStepPointsMap([selectedPoap].filter(Boolean));
    }

    return (teams || []).map((team) => {
      const completed = Array.isArray(team.completedSteps) ? team.completedSteps : [];
      // normalize IDs to string for safe matching
      const achievedPoints = completed.reduce(
        (sum, st) => sum + (stepPointsMap.get(String(st.id)) || 0),
        0
      );

      const pct =
        totalPointsInScope > 0
          ? Math.round((achievedPoints / totalPointsInScope) * 100)
          : 0;

      return {
        ...team,
        // Show achieved points for clarity
        points: achievedPoints,
        progressPct: Math.max(0, Math.min(100, pct)),
        _achievedPointsInTarget: achievedPoints, 
        _totalPointsInTarget: totalPointsInScope, 
      };
    });
  }, [teams, poaps, poapFilter, allPoapsTotalPoints]);

  // ---- Search by team name ----
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return withProgress.filter((t) => !q || t.name?.toLowerCase().includes(q));
  }, [withProgress, query]);

  // ---- Sort by progress (matches UI label) ----
  const sorted = useMemo(
    () =>
      [...filtered].sort((a, b) =>
        sortOrder === "asc" ? a.progressPct - b.progressPct : b.progressPct - a.progressPct
      ),
    [filtered, sortOrder]
  );

  // Reset page when dataset/controls change
  useEffect(() => {
    setPage(0);
  }, [sorted.length, query, poapFilter, sortOrder, PAGE_SIZE]);

  // ---- Pagination ----
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageSlice = useMemo(() => {
    const start = page * PAGE_SIZE;
    return sorted.slice(start, start + PAGE_SIZE);
  }, [sorted, page, PAGE_SIZE]);

  const toggleSort = () => setSortOrder((s) => (s === "asc" ? "desc" : "asc"));

  // Compact page list with ellipses - mobile optimized
  const buildPageList = () => {
    const pages = [];
    const push = (p) => pages.push(p);

    // More compact pagination for mobile
    const maxVisible = isMobile ? 5 : 7;

    if (totalPages <= maxVisible) {
      for (let i = 0; i < totalPages; i++) push(i);
    } else {
      const first = 0;
      const last = totalPages - 1;
      const near = [page - 1, page, page + 1].filter((x) => x > first && x < last);

      push(first);
      if (near[0] > first + 1) push("ellip-left");
      near.forEach(push);
      if (near[near.length - 1] < last - 1) push("ellip-right");
      push(last);
    }
    return pages;
  };

  return (
    <Card extra="p-4 sm:p-6 bg-white dark:bg-navy-800 shadow-sm rounded-2xl">
      {/* Header */}
      <div className="flex flex-col gap-3 mb-5">
        <div className="flex items-center gap-3">
          <h3 className="text-lg sm:text-xl font-semibold text-navy-700 dark:text-white">
            Team Performance
          </h3>
        </div>

        {/* Controls - Mobile Optimized */}
        <div className="flex flex-col gap-3">
          {/* Search - Full width on mobile */}
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search team…"
              className="w-full rounded-xl border border-gray-200 dark:border-navy-600 bg-white/70 dark:bg-navy-700
                         px-3 py-2 text-sm text-navy-700 dark:text-gray-200 placeholder-gray-400 outline-none focus:ring-2 focus:ring-orange-400"
              aria-label="Search team by name"
            />
          </div>

          {/* Filter and Sort - Side by side on mobile */}
          <div className="flex gap-2">
            <select
              value={poapFilter}
              onChange={(e) => setPoapFilter(e.target.value)}
              className="flex-1 rounded-xl border border-gray-200 dark:border-navy-600 bg-white/70 dark:bg-navy-700
                         px-3 py-2 text-sm text-navy-700 dark:text-gray-200 outline-none focus:ring-2 focus:ring-orange-400"
              aria-label="Filter by POAP"
            >
              <option value="All">All POAPs</option>
              {poaps.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>

            <button
              onClick={toggleSort}
              className="flex-shrink-0 rounded-xl bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 text-sm font-medium transition"
              aria-label={`Sort by progress ${sortOrder === "asc" ? "descending" : "ascending"}`}
            >
              <span className="hidden sm:inline">Sort: </span>
              {sortOrder === "asc" ? "↑" : "↓"}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Card View / Desktop Table View */}
      {isMobile ? (
        // Mobile Card Layout
        <div className="space-y-3">
          {loading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={`s-${i}`} className="animate-pulse bg-gray-50 dark:bg-navy-700 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-navy-600" />
                  <div className="h-4 w-32 rounded bg-gray-200 dark:bg-navy-600" />
                </div>
                <div className="space-y-2">
                  <div className="h-3 w-20 rounded bg-gray-200 dark:bg-navy-600" />
                  <div className="h-2 w-full rounded bg-gray-200 dark:bg-navy-600" />
                </div>
              </div>
            ))}

          {!loading && pageSlice.length === 0 && (
            <div className="text-center py-10">
              <p className="text-navy-700 dark:text-gray-200 font-medium">No teams found</p>
            </div>
          )}

          {!loading &&
            pageSlice.map((t, idx) => {
              const pctNum = Math.max(0, Math.min(100, Number(t.progressPct) || 0));
              return (
                <div
                  key={t.id ?? `${t.name}-${idx}`}
                  className="bg-gray-50 dark:bg-navy-700/30 rounded-lg p-4 border border-gray-100 dark:border-navy-700"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-navy-600 grid place-items-center text-xs font-semibold text-gray-700 dark:text-gray-200">
                        {initials(t.name)}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-navy-700 dark:text-white">
                          {t.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          #{page * PAGE_SIZE + idx + 1}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {t.points ?? 0} pts
                      </div>
                      <div className="text-xs text-gray-500">
                        {pctNum}%
                      </div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full h-2 rounded-full bg-gray-200 dark:bg-navy-700 overflow-hidden">
                    <div
                      className="h-2 rounded-full bg-brand-500 transition-all duration-500"
                      style={{ width: `${pctNum}%` }}
                      role="progressbar"
                      aria-valuenow={pctNum}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      ) : (
        // Desktop Table Layout
        <div className="overflow-auto rounded-xl border border-gray-100 dark:border-navy-700">
          <table className="min-w-full bg-white dark:bg-navy-800">
            <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-navy-700/80 backdrop-blur">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                  #
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                  Team
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                  Points
                </th>
                <th
                  className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 cursor-pointer select-none"
                  onClick={toggleSort}
                  title="Sort by progress"
                >
                  Progress {sortOrder === "asc" ? "▲" : "▼"}
                </th>
              </tr>
            </thead>

            <tbody>
              {loading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={`s-${i}`} className="animate-pulse">
                    <td className="px-5 py-4">
                      <div className="h-3 w-4 rounded bg-gray-200 dark:bg-navy-600" />
                    </td>
                    <td className="px-5 py-4">
                      <div className="h-4 w-40 rounded bg-gray-200 dark:bg-navy-600 mb-2" />
                      <div className="h-3 w-24 rounded bg-gray-200 dark:bg-navy-600" />
                    </td>
                    <td className="px-5 py-4">
                      <div className="h-6 w-28 rounded-full bg-gray-200 dark:bg-navy-600" />
                    </td>
                    <td className="px-5 py-4">
                      <div className="h-2 w-full rounded bg-gray-200 dark:bg-navy-600" />
                    </td>
                  </tr>
                ))}

              {!loading && pageSlice.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center">
                    <p className="text-navy-700 dark:text-gray-200 font-medium">No teams found</p>
                  </td>
                </tr>
              )}

              {!loading &&
                pageSlice.map((t, idx) => {
                  const pctNum = Math.max(0, Math.min(100, Number(t.progressPct) || 0));
                  return (
                    <tr
                      key={t.id ?? `${t.name}-${idx}`}
                      className="hover:bg-gray-50 dark:hover:bg-navy-700/60 transition-colors"
                    >
                      <td className="px-5 py-4 text-sm text-gray-600 dark:text-gray-300">
                        {page * PAGE_SIZE + idx + 1}
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-navy-600 grid place-items-center text-xs font-semibold text-gray-700 dark:text-gray-200">
                            {initials(t.name)}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-navy-700 dark:text-white">
                              {t.name}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4 text-sm font-medium text-gray-700 dark:text-gray-200">
                        {t.points ?? 0}
                      </td>

                      <td className="px-5 py-4">
                        {/* Points-based progress bar */}
                        <div className="flex items-center gap-3">
                          <span
                            className="w-12 shrink-0 text-sm font-medium text-gray-700 dark:text-gray-200"
                            title={`${pctNum}%`}
                          >
                            {pctNum}%
                          </span>

                          {/* Track */}
                          <div className="flex-1 min-w-[120px] h-2 rounded-full bg-gray-200 dark:bg-navy-700 overflow-hidden">
                            {/* Fill */}
                            <div
                              className="h-2 rounded-full bg-brand-500 transition-all duration-500"
                              style={{ width: `${pctNum}%` }}
                              role="progressbar"
                              aria-valuenow={pctNum}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination footer */}
      {!loading && sorted.length > PAGE_SIZE && (
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs text-gray-600 dark:text-gray-400 order-2 sm:order-1">
            Showing <span className="font-semibold">{page * PAGE_SIZE + 1}</span>–
            <span className="font-semibold">
              {Math.min((page + 1) * PAGE_SIZE, sorted.length)}
            </span>{" "}
            of <span className="font-semibold">{sorted.length}</span>
          </div>

          <div className="flex items-center gap-1 order-1 sm:order-2">
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 0))}
              disabled={page === 0}
              className="px-2 sm:px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 dark:border-navy-600
                         text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-navy-700
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="hidden sm:inline">Prev</span>
              <span className="sm:hidden">‹</span>
            </button>

            {(() => {
              const pages = buildPageList();
              return pages.map((p, i) =>
                typeof p === "number" ? (
                  <button
                    key={i}
                    onClick={() => setPage(p)}
                    className={`px-2 sm:px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
                      page === p
                        ? "bg-orange-500 text-white border-orange-500"
                        : "border-gray-200 dark:border-navy-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-navy-700"
                    }`}
                  >
                    {p + 1}
                  </button>
                ) : (
                  <span key={i} className="px-1 sm:px-2 text-gray-400">…</span>
                )
              );
            })()}

            <button
              onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
              disabled={page === totalPages - 1}
              className="px-2 sm:px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 dark:border-navy-600
                         text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-navy-700
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="hidden sm:inline">Next</span>
              <span className="sm:inline">›</span>
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default StudentList;